import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { SingleDatePicker } from '@/components/commons/date-picker'
import { InputText } from '@/components/commons/input-text'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogTitle
} from '@/components/ui/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { dayjs } from '@/dayjs'
import { cn, errorHandler } from '@/helpers/utils'
import { type TKey, useTranslation } from '@/translations/i18n'
import { ROADMAP_CATEGORIES, STATUS_STYLES } from '../constants'
import { ActivityFormSchema, type ActivityFormValues } from '../form-schemas'
import { useCreateRoadmapActivity } from '../queries/use-create-roadmap-activity'
import { useDeleteRoadmapActivity } from '../queries/use-delete-roadmap-activity'
import { useUpdateRoadmapActivity } from '../queries/use-update-roadmap-activity'
import {
	ROADMAP_ACTIVITY_STATUS_OPTIONS,
	type RoadmapActivity,
	type RoadmapActivityStatus
} from '../types'

/** The row's display title for a category slug, for the select trigger. */
const categoryLabel = (value: string): string =>
	ROADMAP_CATEGORIES.find((category) => category.id === value)?.title ?? ''

/** Maps a stored activity onto the shape the form fields hold (all strings). */
const toFormValues = (activity: RoadmapActivity): ActivityFormValues => ({
	title: activity.title,
	category: activity.category,
	status: activity.status,
	dateFrom: activity.dateFrom,
	dateTo: activity.dateTo,
	backendPoints: String(activity.backendPoints),
	frontendPoints: String(activity.frontendPoints),
	lead: activity.lead
})

interface DateFieldProps {
	label: string
	value: string
	onChange: (value: string) => void
	errorMessage?: string
}

function DateField({ label, value, onChange, errorMessage }: DateFieldProps) {
	const { t } = useTranslation()

	return (
		<div className='flex flex-1 flex-col gap-1.5'>
			<span className='font-medium text-neutral-900 text-sm'>
				{label}
			</span>
			<SingleDatePicker
				setValue={(date) =>
					onChange(date ? dayjs(date).format('YYYY-MM-DD') : '')
				}
				trigger={
					<button
						className={cn(
							'h-10 rounded-xl border px-3 text-left text-sm',
							errorMessage
								? 'border-danger-300'
								: 'border-grey-400',
							value ? 'text-neutral-900' : 'text-neutral-500'
						)}
						type='button'
					>
						{value
							? dayjs(value).format('L')
							: t('roadmaps:form_date_placeholder')}
					</button>
				}
				value={value ? dayjs(value).toDate() : undefined}
			/>
			{errorMessage ? (
				<small className='text-danger-500'>{errorMessage}</small>
			) : null}
		</div>
	)
}

interface RoadmapActivityDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	/** Present in edit mode, absent when creating. */
	activity?: RoadmapActivity | null
}

/**
 * One dialog for both create and edit: the fields and validation are identical,
 * only the title, the submit label and the presence of delete differ.
 */
function RoadmapActivityDialog({
	open,
	onOpenChange,
	activity
}: RoadmapActivityDialogProps) {
	const { t } = useTranslation()
	const isEdit = !!activity
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors }
	} = useForm<ActivityFormValues>({
		resolver: zodResolver(ActivityFormSchema),
		defaultValues: {
			title: '',
			category: 'luxe',
			status: 'todo',
			dateFrom: '',
			dateTo: '',
			backendPoints: '',
			frontendPoints: '',
			lead: ''
		}
	})

	// The dialog stays mounted between openings, so the fields are refilled
	// whenever the activity behind it changes: without this, opening a second
	// bar would show the first one's values. Editing keeps `keepDefaultValues`
	// so the activity's values fill the fields without becoming the new
	// defaults; otherwise a following create would reset back onto them, since
	// `reset(values)` also adopts those values as defaults. Creating then
	// resets to the empty defaults declared above.
	useEffect(() => {
		if (!open) {
			return
		}
		if (activity) {
			reset(toFormValues(activity), { keepDefaultValues: true })
		} else {
			reset()
		}
		setIsConfirmingDelete(false)
	}, [open, activity, reset])

	const close = () => onOpenChange(false)

	const createActivity = useCreateRoadmapActivity()
	const updateActivity = useUpdateRoadmapActivity()
	const deleteActivity = useDeleteRoadmapActivity()

	const isPending =
		createActivity.isPending ||
		updateActivity.isPending ||
		deleteActivity.isPending

	const onSubmit = handleSubmit((values) => {
		const payload = {
			category: values.category,
			title: values.title.trim(),
			dateFrom: values.dateFrom,
			dateTo: values.dateTo,
			status: values.status,
			backendPoints: Number(values.backendPoints),
			frontendPoints: Number(values.frontendPoints),
			lead: values.lead.trim()
		}

		if (activity) {
			// The activity is located by its original title; `payload` carries
			// the new values, including a possibly-renamed title.
			updateActivity.mutate(
				{ title: activity.title, changes: payload },
				{
					onSuccess: () => {
						toast.success(t('roadmaps:form_updated'))
						close()
					},
					onError: (error) => errorHandler(error, 'code')
				}
			)
			return
		}

		createActivity.mutate(payload, {
			onSuccess: () => {
				toast.success(t('roadmaps:form_created'))
				close()
			},
			onError: (error) => errorHandler(error, 'code')
		})
	})

	const onDelete = () => {
		if (!activity) {
			return
		}
		deleteActivity.mutate(activity.title, {
			onSuccess: () => {
				toast.success(t('roadmaps:form_deleted'))
				close()
			},
			onError: (error) => errorHandler(error, 'code')
		})
	}

	const errorText = (key?: string) => (key ? t(key as TKey) : undefined)

	return (
		<Dialog onOpenChange={onOpenChange} open={open}>
			<DialogContent className='max-w-140'>
				<DialogBody className='gap-4'>
					<div className='flex flex-col gap-1'>
						<DialogTitle>
							{t(
								isEdit
									? 'roadmaps:form_edit_title'
									: 'roadmaps:form_create_title'
							)}
						</DialogTitle>
					</div>

					<div className='flex flex-col gap-5'>
						<InputText
							errorMessage={errorText(errors.title?.message)}
							label={t('roadmaps:form_title_label')}
							placeholder={t('roadmaps:form_title_placeholder')}
							type='text'
							{...register('title')}
						/>

						<div className='flex gap-4'>
							<div className='flex flex-1 flex-col gap-1.5'>
								<span className='font-medium text-neutral-900 text-sm'>
									{t('roadmaps:form_category_label')}
								</span>
								<Controller
									control={control}
									name='category'
									render={({ field }) => (
										<Select
											onValueChange={field.onChange}
											value={field.value}
										>
											<SelectTrigger className='w-full'>
												{/* Show the row's title, not its
												    slug: the value is 'pro_photo',
												    the reader wants "Pro Photo". */}
												<SelectValue>
													{(value: string) =>
														categoryLabel(value)
													}
												</SelectValue>
											</SelectTrigger>
											<SelectContent className='min-w-[var(--anchor-width)]'>
												{ROADMAP_CATEGORIES.map(
													(category) => (
														<SelectItem
															key={category.id}
															value={category.id}
														>
															{category.title}
														</SelectItem>
													)
												)}
											</SelectContent>
										</Select>
									)}
								/>
							</div>

							<div className='flex flex-1 flex-col gap-1.5'>
								<span className='font-medium text-neutral-900 text-sm'>
									{t('roadmaps:form_status_label')}
								</span>
								<Controller
									control={control}
									name='status'
									render={({ field }) => (
										<Select
											onValueChange={field.onChange}
											value={field.value}
										>
											<SelectTrigger className='w-full'>
												<SelectValue>
													{(
														value: RoadmapActivityStatus
													) =>
														t(
															STATUS_STYLES[value]
																.label
														)
													}
												</SelectValue>
											</SelectTrigger>
											<SelectContent className='min-w-[var(--anchor-width)]'>
												{ROADMAP_ACTIVITY_STATUS_OPTIONS.map(
													(status) => (
														<SelectItem
															key={status}
															value={status}
														>
															{t(
																STATUS_STYLES[
																	status
																].label
															)}
														</SelectItem>
													)
												)}
											</SelectContent>
										</Select>
									)}
								/>
							</div>
						</div>

						<div className='flex gap-4'>
							<Controller
								control={control}
								name='dateFrom'
								render={({ field }) => (
									<DateField
										errorMessage={errorText(
											errors.dateFrom?.message
										)}
										label={t(
											'roadmaps:form_date_from_label'
										)}
										onChange={field.onChange}
										value={field.value}
									/>
								)}
							/>
							<Controller
								control={control}
								name='dateTo'
								render={({ field }) => (
									<DateField
										errorMessage={errorText(
											errors.dateTo?.message
										)}
										label={t('roadmaps:form_date_to_label')}
										onChange={field.onChange}
										value={field.value}
									/>
								)}
							/>
						</div>

						<div className='flex gap-4'>
							<InputText
								errorMessage={errorText(
									errors.backendPoints?.message
								)}
								label={t('roadmaps:form_backend_points_label')}
								type='text'
								{...register('backendPoints')}
							/>
							<InputText
								errorMessage={errorText(
									errors.frontendPoints?.message
								)}
								label={t('roadmaps:form_frontend_points_label')}
								type='text'
								{...register('frontendPoints')}
							/>
						</div>

						<InputText
							errorMessage={errorText(errors.lead?.message)}
							label={t('roadmaps:form_lead_label')}
							placeholder={t('roadmaps:form_lead_placeholder')}
							type='text'
							{...register('lead')}
						/>
					</div>
				</DialogBody>

				<DialogFooter className='flex flex-row justify-between'>
					{isConfirmingDelete ? (
						<>
							<span className='self-center text-neutral-600 text-sm'>
								{t('roadmaps:form_delete_confirm')}
							</span>
							<div className='flex gap-2'>
								<Button
									onClick={() => setIsConfirmingDelete(false)}
									type='button'
									variant='tertiary'
								>
									{t('roadmaps:form_cancel')}
								</Button>
								<Button
									disabled={isPending}
									onClick={onDelete}
									type='button'
									variant='primary'
								>
									{t('roadmaps:form_delete')}
									{deleteActivity.isPending && (
										<Spinner size={16} />
									)}
								</Button>
							</div>
						</>
					) : (
						<>
							{isEdit ? (
								<Button
									onClick={() => setIsConfirmingDelete(true)}
									type='button'
									variant='tertiaryUnderlined'
								>
									{t('roadmaps:form_delete')}
								</Button>
							) : (
								<span />
							)}
							<div className='flex gap-2'>
								<Button
									onClick={close}
									type='button'
									variant='tertiary'
								>
									{t('roadmaps:form_cancel')}
								</Button>
								<Button
									disabled={isPending}
									onClick={onSubmit}
									type='button'
									variant='primary'
								>
									{t(
										isEdit
											? 'roadmaps:form_save'
											: 'roadmaps:form_create'
									)}
									{(createActivity.isPending ||
										updateActivity.isPending) && (
										<Spinner size={16} />
									)}
								</Button>
							</div>
						</>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export { RoadmapActivityDialog }
